import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import CompetitionsTabDrillDownModal from './CompetitionsTabDrillDownModal';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#4FC3F7', '#6366F1', '#14B8A6', '#F97316', '#84CC16'];
const AGE_ORDER = ['U08', 'U09', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U19', 'Senior'];

function StatCard({ label, value, sub, color }) {
  return (
    <div className="glass-card" style={{ padding: '1rem 1.25rem', textAlign: 'center' }}>
      <div className="kpi-value" style={{ color: color || 'var(--brand-primary)' }}>{value}</div>
      <div className="kpi-label">{label}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

const CHART_VIEWS = [
  { id: 'ageGroups', label: 'Age Groups' },
  { id: 'byType', label: 'By Category' },
  { id: 'byRegion', label: 'By Region' },
  { id: 'formats', label: 'Formats' },
  { id: 'topComps', label: 'Top Competitions' },
];

/** Shared chart area used by all three sections */
function CategoryCharts({ catData, view, setView, onDrillComp }) {
  const { byType, byRegion, byGender, ageGroups, oversDistribution, topComps } = catData;

  const typeData = Object.values(byType || {}).map(c => ({
    name: `${c.cricket_type} ${c.gender}`, count: c.count,
  }));

  const ageData = AGE_ORDER.map(ag => {
    const found = (ageGroups || []).find(a => a.age_group === ag);
    return { name: ag, count: found ? found.count : 0 };
  }).filter(a => a.count > 0);

  const genderData = Object.entries(byGender || {}).map(([name, v]) => ({ name, value: v.count }));
  const regionData = (byRegion || []).slice(0, 12);
  const oversData = (oversDistribution || []).map(o => ({ name: `${o.overs} overs`, count: o.count }));
  const comps = (topComps || []).slice(0, 15);

  return (
    <>
      <div className="people-pills" style={{ marginBottom: '1rem' }}>
        {CHART_VIEWS.map(v => (
          <button key={v.id} className={`people-pill ${view === v.id ? 'active' : ''}`}
            onClick={() => setView(v.id)} data-testid={`comp-filter-${v.id}`}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'ageGroups' && (
        <div className="charts-grid">
          <div className="glass-card chart-card">
            <h3>Entries by Age Group</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={ageData} margin={{ bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="count" name="Team Entries" radius={[6, 6, 0, 0]}>
                  {ageData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="glass-card chart-card">
            <h3>Gender Distribution</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie data={genderData} cx="50%" cy="50%" innerRadius={60} outerRadius={120}
                  paddingAngle={4} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  <Cell fill="#0B3D91" /><Cell fill="#E31837" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {view === 'byType' && (
        <div className="glass-card chart-card">
          <h3>Entries by Category &amp; Gender</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={typeData} layout="vertical" margin={{ left: 140 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="count" fill="#0B3D91" radius={[0, 6, 6, 0]} name="Entries">
                {typeData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === 'byRegion' && (
        <div className="glass-card chart-card">
          <h3>Competition Entries by Region</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={regionData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="region" tick={{ fontSize: 10, angle: -35 }} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="count" fill="#0B3D91" radius={[4, 4, 0, 0]} name="Entries" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === 'formats' && (
        <div className="glass-card chart-card">
          <h3>Overs Format Distribution</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={oversData} margin={{ bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="count" name="Entries" radius={[6, 6, 0, 0]}>
                {oversData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === 'topComps' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'auto' }}>
          <div style={{ padding: '1rem 1.25rem 0' }}>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700 }}>
              Top Competitions by Team Entries
            </h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Each entry represents a unique team participating in a competition.{' '}
              <strong style={{ color: 'var(--text-secondary)' }}>Click a row</strong> for regional and category breakdown.
            </p>
          </div>
          <table className="data-table" data-testid="top-competitions-table">
            <thead>
              <tr>
                <th>#</th><th>Competition</th><th className="num">Team Entries</th>
              </tr>
            </thead>
            <tbody>
              {comps.map((c, i) => (
                <tr
                  key={i}
                  className="drill-row-clickable"
                  onClick={() => onDrillComp({ name: c.name })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrillComp({ name: c.name }); }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td className="num" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{c.teams}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default function CompetitionCharts({ data }) {
  const [section, setSection] = useState('combined'); // 'hardball' | 'modified' | 'combined'
  const [view, setView] = useState('ageGroups');
  const [drillComp, setDrillComp] = useState(null);

  const {
    competition_by_type, competition_by_region, gender_split, pathway,
    age_groups, overs_distribution,
    unique_teams, unique_competitions, top_competitions, summary,
    competitions_hardball, competitions_modified,
    competitions_combined_total_teams, competitions_combined_total_players,
    _competitions: competitions,
  } = data;

  // Build combined stats object in the same shape as buildCategoryStats output
  const combinedStats = {
    byType: competition_by_type || {},
    byRegion: competition_by_region || [],
    byGender: gender_split || {},
    ageGroups: age_groups || [],
    oversDistribution: overs_distribution || [],
    topComps: top_competitions || [],
    totalTeams: competitions_combined_total_teams || 0,
    totalPlayers: competitions_combined_total_players || 0,
    playersPerTeam: null,
    juniorSeniorSplit: null, // combined view doesn't show this panel
  };

  const sections = [
    { id: 'hardball',  label: 'Hardball',      color: '#0B3D91', desc: 'Junior & Senior · 15 players/team' },
    { id: 'modified',  label: 'Modified Ball',  color: '#E31837', desc: 'Male & Female · 8 players/team' },
    { id: 'combined',  label: 'Combined',       color: '#10B981', desc: 'All categories' },
  ];

  const activeCatData =
    section === 'hardball' ? competitions_hardball :
    section === 'modified' ? competitions_modified :
    combinedStats;

  const genderSplit = activeCatData?.byGender || {};
  // byGender from buildCategoryStats stores { count } objects; combined gender_split also uses { count }
  const maleCount   = genderSplit?.Male?.count ?? genderSplit?.Male ?? 0;
  const femaleCount = genderSplit?.Female?.count ?? genderSplit?.Female ?? 0;

  const totalTeams   = activeCatData?.totalTeams || 0;
  const totalPlayers = activeCatData?.totalPlayers || 0;
  const ppt          = activeCatData?.playersPerTeam;

  const handleSectionChange = (id) => {
    setSection(id);
    setView('ageGroups');
  };

  return (
    <div data-testid="competition-section">
      <div className="section-title">Competition Analytics</div>

      {/* Section switcher */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => handleSectionChange(s.id)}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: 8,
              border: `2px solid ${section === s.id ? s.color : 'rgba(255,255,255,0.15)'}`,
              background: section === s.id ? s.color : 'rgba(255,255,255,0.05)',
              color: section === s.id ? '#fff' : 'var(--text-secondary)',
              fontWeight: section === s.id ? 700 : 500,
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {s.label}
            <span style={{ fontSize: '0.65rem', display: 'block', opacity: 0.8, fontWeight: 400 }}>
              {s.desc}
            </span>
          </button>
        ))}
      </div>

      {/* KPI row — adapts to the active section */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: '1.25rem' }}>
        <StatCard label="Team Entries" value={totalTeams.toLocaleString()} />
        <StatCard
          label="Total Players"
          value={totalPlayers.toLocaleString()}
          sub={ppt ? `${ppt} per team` : 'Hardball ×15 + Modified ×8'}
          color="#E31837"
        />
        <StatCard label="Players/Team" value={ppt ?? '8 / 15'} color="#2196F3" />
        <StatCard label="Male Entries" value={maleCount.toLocaleString()} color="#0B3D91" />
        <StatCard label="Female Entries" value={femaleCount.toLocaleString()} color="#E31837" />
      </div>

      {/* Junior / Senior breakdown — shown for Hardball and Modified Ball sections */}
      {activeCatData?.juniorSeniorSplit && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {[
            {
              label: section === 'modified' ? 'Junior Modified (U08 – U13)' : 'Junior Hardball',
              color: '#2196F3',
              data: activeCatData.juniorSeniorSplit.junior,
            },
            {
              label: section === 'modified' ? 'Senior Modified (U14+)' : 'Senior Hardball',
              color: '#0B3D91',
              data: activeCatData.juniorSeniorSplit.senior,
            },
          ].map(({ label, color, data }) => (
            <div key={label} className="glass-card" style={{ padding: '1rem 1.25rem', borderLeft: `3px solid ${color}` }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color, marginBottom: '0.6rem' }}>{label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{data.teams.toLocaleString()}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Teams</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#E31837' }}>{data.players.toLocaleString()}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Players</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0B3D91' }}>{data.male.toLocaleString()}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Male</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#E31837' }}>{data.female.toLocaleString()}</div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>Female</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeCatData && (
        <CategoryCharts
          catData={activeCatData}
          view={view}
          setView={setView}
          onDrillComp={setDrillComp}
        />
      )}

      <CompetitionsTabDrillDownModal
        open={drillComp != null}
        onClose={() => setDrillComp(null)}
        variant={drillComp ? 'competition' : null}
        competitionName={drillComp?.name}
        competitions={competitions || []}
      />
    </div>
  );
}
